import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Edit } from "lucide-react";
import { PropertyContract } from "@/hooks/useContracts";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { format } from "date-fns";
import { es } from "date-fns/locale";

interface ContractsTableProps {
  contracts: PropertyContract[];
  isLoading: boolean;
  onEdit: (contract: PropertyContract) => void;
}

const ContractsTable = ({ contracts, isLoading, onEdit }: ContractsTableProps) => {
  const { data: properties } = useProperties();
  const { data: organizations } = useOrganizations();

  const getPropertyName = (propertyId: string) => {
    const property = properties?.find(p => p.id === propertyId);
    return property?.shortName || property?.title || propertyId;
  };

  const getOrganizationName = (organizationId: string | null | undefined) => {
    if (!organizationId) return "—";
    const org = organizations?.find(o => o.id === organizationId);
    return org?.name || "—";
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return `${value}%`;
  };

  const calculateRentalTotal = (amount: number, ivaPercentage: number) => {
    const ivaAmount = amount * (ivaPercentage / 100);
    return amount + ivaAmount;
  };

  if (isLoading) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">Cargando contratos...</p>
      </div>
    );
  }

  if (contracts.length === 0) {
    return (
      <div className="border rounded-lg p-8 text-center">
        <p className="text-muted-foreground">No hay contratos registrados</p>
      </div>
    );
  }

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Propiedad</TableHead>
            <TableHead>Inicio de Operación</TableHead>
            <TableHead>Tarifa Limpieza</TableHead>
            <TableHead>Tarifa Admin.</TableHead>
            <TableHead>Comisión Admin.</TableHead>
            <TableHead>Tipo de Admin.</TableHead>
            <TableHead>Organización</TableHead>
            <TableHead>Estado</TableHead>
            <TableHead className="w-24">Acciones</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {contracts.map((contract) => (
            <TableRow key={contract.id}>
              <TableCell className="font-medium">
                {getPropertyName(contract.property_id)}
              </TableCell>
              <TableCell>
                {format(new Date(contract.operation_start_date + 'T12:00:00'), 'dd/MM/yy', { locale: es })}
              </TableCell>
              <TableCell>
                {formatCurrency(contract.cleaning_rate)}
              </TableCell>
              <TableCell>
                {formatCurrency(contract.administration_rate)}
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <div>{formatPercentage(contract.administration_commission)}</div>
                  {contract.promotional_commission && contract.promotion_end_date && (
                    <div className="text-sm text-orange-600">
                      Promo: {formatPercentage(contract.promotional_commission)} 
                       {new Date(contract.promotion_end_date + 'T12:00:00') > new Date() && (
                         <span className="ml-1">
                           (hasta {format(new Date(contract.promotion_end_date + 'T12:00:00'), 'dd/MM/yy', { locale: es })})
                         </span>
                      )}
                    </div>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="space-y-1">
                  <Badge variant="outline">
                    {contract.admin_type}
                  </Badge>
                  {contract.admin_type === 'Renta' && 
                   contract.rental_payment_day && 
                   contract.rental_monthly_amount !== undefined && 
                   contract.rental_iva_percentage !== undefined && (
                    <div className="text-sm text-muted-foreground">
                      Día {String(contract.rental_payment_day).padStart(2, '0')} del mes - {formatCurrency(
                        calculateRentalTotal(
                          contract.rental_monthly_amount, 
                          contract.rental_iva_percentage
                        )
                      )}
                    </div>
                  )}
                 </div>
               </TableCell>
               <TableCell>
                 <span className="text-sm">
                   {getOrganizationName(contract.organization_id)}
                 </span>
               </TableCell>
               <TableCell>
                 <Badge variant={contract.status === 'active' ? 'default' : 'secondary'}>
                   {contract.status === 'active' ? 'Activo' : 'Cancelado'}
                 </Badge>
               </TableCell>
              <TableCell>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onEdit(contract)}
                >
                  <Edit className="h-4 w-4" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default ContractsTable;